import React, { useState } from "react"
import { X, Send, Loader2 } from "lucide-react"
import { sendEmail } from "../services/emailService"

const ContactPanel = ({ isOpen, onClose, userEmail, userName, userId }) => {
  const [formData, setFormData] = useState({
    from: "", // User's email
    to: userEmail || "", // Candidate's email (pre-filled)
    subject: "",
    message: "",
  })

  const [isSubmitting, setIsSubmitting] = useState(false)
  const [submitStatus, setSubmitStatus] = useState(null) // 'success' | 'error' | null

  if (!isOpen) return null

  const handleSubmit = async (e) => {
    e.preventDefault()

    // Validate required fields
    if (
      !formData.from ||
      !formData.to ||
      !formData.subject ||
      !formData.message
    ) {
      setSubmitStatus("error")
      return
    }

    setIsSubmitting(true)
    setSubmitStatus(null)

    try {
      // Send email via the backend service
      await sendEmail({
        email: formData.to, // Recipient (candidate)
        subject: formData.subject,
        text: formData.message,
        html: `<p>${formData.message.replace(/\n/g, "<br>")}</p>`,
        cc: formData.from, // CC the sender
        conversationId: userId, // Pass userId as conversationId for context
        recipientEmail: formData.to,
        senderName: formData.from,
      })

      setSubmitStatus("success")

      // Reset form and close panel after success
      setTimeout(() => {
        setFormData({
          from: "",
          to: userEmail || "",
          subject: "",
          message: "",
        })
        setSubmitStatus(null)
        onClose()
      }, 1500)
    } catch (error) {
      console.error("Failed to send email:", error)
      setSubmitStatus("error")
    } finally {
      setIsSubmitting(false)
    }
  }

  const handleChange = (e) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value,
    })
    // Clear error status when user starts typing
    if (submitStatus === "error") {
      setSubmitStatus(null)
    }
  }

  return (
    <>
      {/* Backdrop - Full screen on mobile */}
      <div
        className="fixed inset-0 bg-black/50 z-40 md:hidden"
        onClick={onClose}
      />

      {/* Contact Panel */}
      <div className="fixed inset-0 md:inset-auto md:bottom-0 md:right-6 w-full md:w-[520px] h-full md:h-[600px] bg-white shadow-2xl md:border md:border-gray-300 md:rounded-t-lg transition-all duration-200 z-50">
        {/* Header */}
        <div className="bg-[#f2f6fc] text-white px-4 py-3 md:py-3 flex items-center justify-between md:rounded-t-lg">
          <h3 className="text-base md:text-sm font-medium text-gray-800">
            New Message
          </h3>
          <button
            onClick={onClose}
            className="hover:bg-white/10 p-1.5 md:p-1 rounded transition-colors text-gray-800"
            title="Close"
            disabled={isSubmitting}
          >
            <X size={20} className="md:w-4 md:h-4" />
          </button>
        </div>

        {/* Content */}
        <form
          onSubmit={handleSubmit}
          className="flex flex-col h-[calc(100%-52px)] md:h-[calc(100%-52px)]"
        >
          {/* Form Fields */}
          <div className="flex-1 overflow-y-auto">
            {/* From Field (User's Email) */}
            <div className="border-b border-gray-200">
              <div className="flex flex-col md:flex-row md:items-center px-4 py-3">
                <label className="text-sm text-gray-600 md:w-16 md:flex-shrink-0 mb-1 md:mb-0">
                  From
                </label>
                <input
                  type="email"
                  name="from"
                  value={formData.from}
                  onChange={handleChange}
                  className="flex-1 text-sm md:text-sm outline-none text-black placeholder-gray-400"
                  placeholder="your.email@example.com"
                  required
                  disabled={isSubmitting}
                />
              </div>
            </div>

            {/* To Field (Candidate's Email - Pre-filled) */}
            <div className="border-b border-gray-200 bg-gray-50/50">
              <div className="flex flex-col md:flex-row md:items-center px-4 py-3">
                <label className="text-sm text-gray-600 md:w-16 md:flex-shrink-0 mb-1 md:mb-0">
                  To
                </label>
                <input
                  type="email"
                  name="to"
                  value={formData.to}
                  onChange={handleChange}
                  className="flex-1 text-sm md:text-sm outline-none text-black bg-transparent"
                  placeholder="Recipient email"
                  required
                  disabled={isSubmitting}
                  readOnly
                />
              </div>
            </div>

            {/* Subject Field */}
            <div className="border-b border-gray-200">
              <div className="flex flex-col md:flex-row md:items-center px-4 py-3">
                <label className="text-sm text-gray-600 md:w-16 md:flex-shrink-0 mb-1 md:mb-0">
                  Subject
                </label>
                <input
                  type="text"
                  name="subject"
                  value={formData.subject}
                  onChange={handleChange}
                  className="flex-1 text-sm md:text-sm outline-none text-black placeholder-gray-400"
                  placeholder="Enter subject"
                  required
                  disabled={isSubmitting}
                />
              </div>
            </div>

            {/* Message Field */}
            <div className="px-4 py-3 pb-24 md:pb-24">
              <textarea
                name="message"
                value={formData.message}
                onChange={handleChange}
                className="w-full h-full min-h-[250px] md:min-h-[300px] text-base md:text-sm outline-none resize-none text-black placeholder-gray-400"
                placeholder="Type your message here..."
                style={{ fontFamily: "Arial, sans-serif" }}
                required
                disabled={isSubmitting}
              />
            </div>
          </div>

          {/* Footer with Send Button */}
          <div className="border-t border-gray-200 px-4 py-3 md:py-3 bg-white">
            <div className="flex flex-col md:flex-row items-stretch md:items-center justify-between gap-3 md:gap-0">
              <div className="flex-1 order-2 md:order-1">
                {submitStatus === "success" && (
                  <span className="text-sm text-green-600 font-medium block text-center md:text-left">
                    ✓ Email sent successfully!
                  </span>
                )}
                {submitStatus === "error" && (
                  <span className="text-sm text-red-600 font-medium block text-center md:text-left">
                    ✗ Failed to send email. Please try again.
                  </span>
                )}
              </div>
              <button
                type="submit"
                disabled={isSubmitting}
                className="px-6 py-3 md:py-2 bg-[#0b57d0] text-white text-base md:text-sm font-medium rounded-full hover:shadow-md transition-all disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2 order-1 md:order-2"
              >
                {isSubmitting ? (
                  <>
                    <Loader2 size={18} className="md:w-4 md:h-4 animate-spin" />
                    Sending...
                  </>
                ) : (
                  <>
                    <Send size={18} className="md:w-4 md:h-4" />
                    Send
                  </>
                )}
              </button>
            </div>
          </div>
        </form>
      </div>
    </>
  )
}

export default ContactPanel